import { type TFunction } from 'i18next';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import TemplateStepper from '~/app/components/integrations/IntegrationTemplateWizard/TemplateStepper/TemplateStepper';
import {
  type GettingStartedStep,
  type StepValues
} from '~/common/AxeMCPServerAnalytics';
import LiveRegionAnnouncement from '~/common/components/LiveRegionAnnouncement';
import PageTitle from '~/common/components/PageTitle';
import { ProductSlugs } from '~/common/constants';
import { useAnalyticsInstance } from '~/common/contexts/analytics';
import { useAuthContext } from '~/common/contexts/auth';
import useWebStorage, { WebStorageType } from '~/common/hooks/useWebStorage';
import { downloadBlob } from '~/common/utils/downloadBlob';
import { getLandingProperties } from '~/common/utils/getLandingProperties';
import cursorIcon from './assets/cursor.png';
import vscodeIcon from './assets/vscode.png';
import styles from './AxeMCPServerSetup.css';
import {
  type EntryPath,
  type FlowAction,
  flowReducer,
  initialFlowState,
  parseFlowState,
  type Screen
} from './flow';
import {
  type AuthMethod,
  buildManualGuide,
  buildPluginGuide,
  type ClientId,
  type Distribution,
  getClientLabels,
  type Guide
} from './guide';
import GuideView from './GuideView';
import SelectionStep, { type SelectionStepConfig } from './SelectionStep';
import { toMarkdown } from './toMarkdown';

const API_KEYS_URL = '/settings';

const STORAGE_KEY = 'axeMCPServerGettingStarted';

const STEPPER_SCREENS: Screen[] = ['auth', 'distribution', 'client', 'guide'];

const DOWNLOAD_FILENAMES: Record<EntryPath, string> = {
  manual: 'axe-mcp-server-setup-guide.md',
  plugin: 'axe-mcp-server-plugin-guide.md'
};

const getStepConfigs = (t: TFunction) => ({
  entry: {
    groupName: 'entry-path',
    heading: t('How do you want to set this up?'),
    sub: t(
      'Both paths set up the same server. The difference is who writes the configuration file.'
    ),
    nextLabel: t('Continue'),
    options: [
      {
        value: 'manual',
        label: t('Configure it yourself'),
        description: t(
          'Best if you do not use Claude Code, or you want a configuration file you can read and keep. Four steps, and you paste the result into your client.'
        )
      },
      {
        value: 'plugin',
        label: t('Use the axe-accessibility plugin for Claude Code'),
        description: t(
          'Best if you already use Claude Code. Install the plugin and run it, and it writes the configuration for you. From inside Claude Code it can also write the configuration for Cursor, VS Code with Copilot, and Claude Desktop.'
        )
      }
    ]
  } satisfies SelectionStepConfig<EntryPath>,
  auth: {
    groupName: 'auth',
    heading: t('How do you want to authenticate?'),
    backLabel: t('Back to setup'),
    nextLabel: t('Next to distribution'),
    options: [
      {
        value: 'apiKey',
        label: t('API key'),
        description: t(
          'Best for the quickest setup: generate one key and paste it into your config, with no extra auth tooling to install. The key never expires, so it stays in that file until you revoke it. It is also the only option where there is no browser to sign in with, such as CI.'
        )
      },
      {
        value: 'oauth',
        label: t('Sign in with your browser (OAuth)'),
        description: t(
          'Best for avoiding a long-lived secret in a config file. The @deque/axe-auth helper signs you in through the browser and stores a short-lived token in your operating system keychain, so your config references the helper instead of holding a credential. Requires installing that helper and signing in again when the token expires.'
        )
      }
    ]
  } satisfies SelectionStepConfig<AuthMethod>,
  distribution: {
    groupName: 'distribution',
    heading: t('How do you want to run the server?'),
    sub: t(
      'Both run the same server. Pick the one your machine is already set up for.'
    ),
    backLabel: t('Back to authentication'),
    nextLabel: t('Next to MCP client'),
    options: [
      {
        value: 'npm',
        label: 'npm',
        description: t(
          'Best for staying current with no upkeep: npx fetches the latest server on every start. Requires Node.js 22.19.0 or later, plus a one-time Chromium install.'
        )
      },
      {
        value: 'docker',
        label: 'Docker',
        description: t(
          'Best for a self-contained setup: the image already has Chromium, so you do not install a browser yourself. Requires Docker running locally and an image pull, plus a re-pull when you want a newer version.'
        )
      }
    ]
  } satisfies SelectionStepConfig<Distribution>,
  client: {
    groupName: 'client',
    heading: t('Which MCP client are you using?'),
    sub: t(
      'The setup guide uses the configuration format your client expects.'
    ),
    backLabel: t('Back to distribution'),
    nextLabel: t('Next to setup guide'),
    options: [
      { value: 'vscode', label: getClientLabels(t).vscode, icon: vscodeIcon },
      { value: 'cursor', label: getClientLabels(t).cursor, icon: cursorIcon },
      {
        value: 'other',
        label: getClientLabels(t).other,
        dividerBefore: true,
        description: t(
          'Any other MCP client. You get the standard mcpServers configuration.'
        )
      }
    ]
  } satisfies SelectionStepConfig<ClientId>
});

const AxeMCPServerSetup = (): React.ReactElement => {
  const { t } = useTranslation();
  const location = useLocation();
  const analytics = useAnalyticsInstance(ProductSlugs.axeMCPServer);
  const { user } = useAuthContext();
  const [state, setState, clearState] = useWebStorage(
    WebStorageType.Session,
    STORAGE_KEY,
    initialFlowState,
    { deserialize: parseFlowState }
  );
  const dispatch = (action: FlowAction) =>
    setState(prev => flowReducer(prev, action));
  const headingRef = useRef<HTMLHeadingElement>(null);
  const focusedScreen = useRef<Screen>(state.screen);

  useEffect(() => {
    // Runs before AnalyticsProvider's effect, which would forget too late.
    analytics.forgetUser(user?.id);
    analytics.viewGettingStarted(
      getLandingProperties({
        search: location.search,
        referrer: document.referrer,
        origin: window.location.origin,
        pathname: location.pathname
      })
    );
  }, []);

  useEffect(() => {
    if (focusedScreen.current === state.screen) {
      return;
    }
    focusedScreen.current = state.screen;
    headingRef.current?.focus();
  }, [state.screen]);

  const steps = useMemo(() => getStepConfigs(t), [t]);
  const manualGuide = useMemo(
    () => buildManualGuide(state.selections, t, API_KEYS_URL),
    [state.selections, t]
  );
  const pluginGuide = useMemo(() => buildPluginGuide(t, API_KEYS_URL), [t]);
  const guides: Record<EntryPath, Guide> = {
    manual: manualGuide,
    plugin: pluginGuide
  };

  const back = () => dispatch({ type: 'back' });
  const completeStep = <K extends GettingStartedStep>(
    step: K,
    value: StepValues[K]
  ) => {
    analytics.stepCompleted<K>({ step, value });
    dispatch({ type: 'next' });
  };

  const renderSelection = <K extends GettingStartedStep>(
    step: K,
    config: SelectionStepConfig<StepValues[K]>,
    value: StepValues[K],
    onChange: (value: StepValues[K]) => void
  ) => (
    <SelectionStep<StepValues[K]>
      config={config}
      value={value}
      onChange={onChange}
      onBack={back}
      onNext={() => completeStep(step, value)}
      onCancel={clearState}
      headingRef={headingRef}
    />
  );

  const renderGuide = (path: EntryPath, backLabel: string) => (
    <GuideView
      guide={guides[path]}
      backLabel={backLabel}
      onBack={back}
      onDownload={() => {
        downloadBlob(
          new Blob(
            [toMarkdown(guides[path], { origin: window.location.origin, t })],
            { type: 'text/markdown;charset=utf-8' }
          ),
          DOWNLOAD_FILENAMES[path]
        );
        analytics.guideDownloaded({ guide: path });
      }}
      onReset={() => dispatch({ type: 'reset' })}
      headingRef={headingRef}
    />
  );

  const { selections } = state;
  const screens: Record<Screen, { title: string; content: React.ReactNode }> = {
    entry: {
      title: steps.entry.heading,
      content: renderSelection('entry', steps.entry, state.path, path =>
        dispatch({ type: 'selectPath', path })
      )
    },
    auth: {
      title: steps.auth.heading,
      content: renderSelection('auth', steps.auth, selections.auth, value =>
        dispatch({ type: 'select', field: 'auth', value })
      )
    },
    distribution: {
      title: steps.distribution.heading,
      content: renderSelection(
        'distribution',
        steps.distribution,
        selections.distribution,
        value => dispatch({ type: 'select', field: 'distribution', value })
      )
    },
    client: {
      title: steps.client.heading,
      content: renderSelection(
        'client',
        steps.client,
        selections.client,
        value => dispatch({ type: 'select', field: 'client', value })
      )
    },
    guide: {
      title: guides.manual.title,
      content: renderGuide('manual', t('Back to MCP client'))
    },
    plugin: {
      title: guides.plugin.title,
      content: renderGuide('plugin', t('Back to setup'))
    }
  };
  const current = screens[state.screen];
  const stepperIndex = STEPPER_SCREENS.indexOf(state.screen);
  const stepperHeaders = [
    t('Authentication'),
    t('Distribution'),
    t('MCP client'),
    t('Setup guide')
  ];

  return (
    <>
      <PageTitle title={`${current.title} | Axe MCP Server`} />
      <div className={styles.wrap}>
        {stepperIndex !== -1 && (
          <div className={styles.stepperWrap}>
            <TemplateStepper
              currentStep={stepperIndex}
              steps={stepperHeaders.map(header => ({ header }))}
            />
          </div>
        )}
        <LiveRegionAnnouncement
          message={
            stepperIndex === -1
              ? ''
              : t('Step {{step}} of {{total}}: {{header}}', {
                  step: stepperIndex + 1,
                  total: stepperHeaders.length,
                  header: stepperHeaders[stepperIndex]
                })
          }
        />
        {/* Keyed so each step gets a new heading: screen readers ignore focus moving to a reused node. */}
        <React.Fragment key={state.screen}>{current.content}</React.Fragment>
      </div>
    </>
  );
};

export default AxeMCPServerSetup;
